/**
 * Google Picker (Drive) — explicit file/folder selection for verification-friendly OAuth (`drive.file`).
 * Top-level tabs: **My Drive**, **Shared with me** (`setOwnedByMe(false)`), **Shared drives**.
 * Requires a browser API key with the Picker API enabled (Console → APIs & Services → Credentials).
 */

import { getGooglePickerAppId, getGooglePickerDeveloperKey } from '../config/google';

let gapiScriptPromise = null;
let pickerApiPromise = null;

function loadGapiScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.gapi?.load) return Promise.resolve();

  if (gapiScriptPromise) return gapiScriptPromise;

  gapiScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-deckpack-gapi-api]');
    if (existing) {
      const done = () => (window.gapi?.load ? resolve() : reject(new Error('gapi incomplete')));
      if (window.gapi?.load) {
        done();
        return;
      }
      existing.addEventListener('load', () => done());
      existing.addEventListener('error', () => reject(new Error('gapi script error')));
      return;
    }

    const s = document.createElement('script');
    s.src = 'https://apis.google.com/js/api.js';
    s.async = true;
    s.defer = true;
    s.dataset.deckpackGapiApi = '1';
    s.onload = () => {
      if (window.gapi?.load) resolve();
      else reject(new Error('gapi not available after load'));
    };
    s.onerror = () => reject(new Error('Failed to load Google API script'));
    document.head.appendChild(s);
  });

  return gapiScriptPromise;
}

function loadPickerApi() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.google?.picker) return Promise.resolve();

  if (pickerApiPromise) return pickerApiPromise;

  pickerApiPromise = loadGapiScript().then(
    () =>
      new Promise((resolve, reject) => {
        try {
          window.gapi.load('picker', {
            callback: () => {
              if (window.google?.picker) resolve();
              else reject(new Error('Picker API did not initialize'));
            },
            onerror: () => reject(new Error('Picker API load error')),
          });
        } catch (e) {
          reject(e);
        }
      })
  );

  return pickerApiPromise;
}

function requirePickerKey() {
  const key = getGooglePickerDeveloperKey();
  if (!key) {
    throw new Error(
      'Missing Google Picker API key. Set REACT_APP_GOOGLE_PICKER_API_KEY (browser key with Picker API enabled).'
    );
  }
  return key;
}

function trySetViewLabel(view, label) {
  try {
    if (label && typeof view.setLabel === 'function') view.setLabel(label);
  } catch {
    /* ignore */
  }
}

/**
 * Let React unmount overlays (modals/dialogs) so the Picker iframe is not hidden underneath.
 * Double rAF + a short delay so portals (e.g. app dialogs) finish painting before `setVisible(true)`.
 */
export function deferPickerOpen(extraMs = 200) {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(resolve, typeof extraMs === 'number' ? extraMs : 200);
      });
    });
  });
}

function trySetOwnedByMe(view, ownedByMe) {
  try {
    if (typeof view.setOwnedByMe === 'function') view.setOwnedByMe(ownedByMe);
  } catch {
    /* ignore */
  }
}

function addMyDriveSharedWithMeAndSharedDrivesTabs(builder, googlePicker, { mimeTypes, folderPick, listMode }) {
  const List = listMode || googlePicker.DocsViewMode.LIST;

  let myDriveView;
  let sharedWithMeView;
  let sharedDrivesView;

  if (folderPick) {
    myDriveView = new googlePicker.DocsView()
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(mimeTypes)
      .setMode(List);

    sharedWithMeView = new googlePicker.DocsView(googlePicker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(mimeTypes)
      .setMode(List);

    sharedDrivesView = new googlePicker.DocsView()
      .setEnableDrives(true)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(mimeTypes)
      .setMode(List);
  } else {
    myDriveView = new googlePicker.DocsView(googlePicker.ViewId.DOCS)
      .setMimeTypes(mimeTypes)
      .setMode(List)
      .setIncludeFolders(false);

    sharedWithMeView = new googlePicker.DocsView(googlePicker.ViewId.DOCS)
      .setMimeTypes(mimeTypes)
      .setMode(List)
      .setIncludeFolders(false);

    sharedDrivesView = new googlePicker.DocsView(googlePicker.ViewId.DOCS)
      .setEnableDrives(true)
      .setMimeTypes(mimeTypes)
      .setMode(List)
      .setIncludeFolders(false);
  }

  trySetOwnedByMe(sharedWithMeView, false);

  trySetViewLabel(myDriveView, 'My Drive');
  trySetViewLabel(sharedWithMeView, 'Shared with me');
  trySetViewLabel(sharedDrivesView, 'Shared drives');

  builder.addView(myDriveView).addView(sharedWithMeView).addView(sharedDrivesView);
}

/**
 * Google Slides — My Drive (nav includes Shared with me) + Shared drives tab. LIST = top-down rows.
 * @param {{ accessToken: string, title?: string, multiselect?: boolean }} opts
 * @returns {Promise<Array<{ id: string, name: string, mimeType?: string }>>} empty if cancelled
 */
export async function openGoogleSlidesPresentationPicker(opts) {
  const { accessToken, title = 'Select presentations', multiselect = true } = opts;
  if (!accessToken) throw new Error('Missing access token');

  await loadPickerApi();
  const developerKey = requirePickerKey();
  const appId = getGooglePickerAppId();

  const googlePicker = window.google.picker;

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      reject(err);
    };

    const builder = new googlePicker.PickerBuilder()
      .setOAuthToken(accessToken)
      .setDeveloperKey(developerKey)
      .setTitle(title)
      .setCallback((data) => {
        try {
          const action = data[googlePicker.Response.ACTION];
          if (action === googlePicker.Action.PICKED) {
            const docs = data[googlePicker.Response.DOCUMENTS] || [];
            const out = docs.map((d) => {
              const id =
                d[googlePicker.Document.ID] ??
                d.id ??
                d.ID ??
                (typeof d === 'object' && d && 'id' in d ? d.id : null);
              const name =
                d[googlePicker.Document.NAME] ??
                d.name ??
                d.NAME ??
                'Untitled';
              const mimeType =
                d[googlePicker.Document.MIME_TYPE] ?? d.mimeType ?? d.mime_type;
              return { id, name, mimeType };
            })
              .map((x) => ({ ...x, id: x.id != null ? String(x.id) : '' }))
              .filter((x) => x.id.length > 0);
            finish(out);
            return;
          }
          if (action === googlePicker.Action.CANCEL) {
            finish([]);
            return;
          }
          if (action === googlePicker.Action.ERROR) {
            const detail = data[googlePicker.Response.ERROR];
            fail(
              new Error(
                detail != null && detail !== ''
                  ? String(detail)
                  : 'Google Picker reported an error.'
              )
            );
            return;
          }
          // Picker also emits non-terminal events (e.g. view/load). Resolving here
          // used to settle the Promise before the user picked — selections were ignored.
          return;
        } catch (e) {
          fail(e);
        }
      });

    addMyDriveSharedWithMeAndSharedDrivesTabs(builder, googlePicker, {
      mimeTypes: 'application/vnd.google-apps.presentation',
      folderPick: false,
      listMode: googlePicker.DocsViewMode.LIST,
    });

    if (appId) builder.setAppId(appId);
    if (multiselect) {
      try {
        const f = googlePicker.Feature?.MULTISELECT_ENABLED;
        if (f != null) builder.enableFeature(f);
      } catch {
        /* ignore */
      }
    }

    try {
      builder.build().setVisible(true);
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Choose a folder (My Drive + Shared drives). LIST mode, full Drive navigation in the widget.
 * @param {{ accessToken: string, title?: string }} opts
 * @returns {Promise<{ id: string, name: string } | null>} null if cancelled
 */
export async function openGoogleDriveFolderPicker(opts) {
  const { accessToken, title = 'Select a folder' } = opts;
  if (!accessToken) throw new Error('Missing access token');

  await loadPickerApi();
  const developerKey = requirePickerKey();
  const appId = getGooglePickerAppId();

  const googlePicker = window.google.picker;

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      reject(err);
    };

    const builder = new googlePicker.PickerBuilder()
      .setOAuthToken(accessToken)
      .setDeveloperKey(developerKey)
      .setTitle(title)
      .setCallback((data) => {
        try {
          const action = data[googlePicker.Response.ACTION];
          if (action === googlePicker.Action.PICKED) {
            const docs = data[googlePicker.Response.DOCUMENTS] || [];
            const d = docs[0];
            if (!d) {
              finish(null);
              return;
            }
            // Match slide picker: folder rows sometimes expose id on `d.id` only.
            const idRaw =
              d[googlePicker.Document.ID] ??
              d.id ??
              d.ID ??
              (typeof d === 'object' && d && 'id' in d ? d.id : null);
            const id = idRaw != null ? String(idRaw) : '';
            const name =
              d[googlePicker.Document.NAME] ??
              d.name ??
              d.NAME ??
              'Folder';
            if (!id) {
              finish(null);
              return;
            }
            finish({ id, name });
            return;
          }
          if (action === googlePicker.Action.CANCEL) {
            finish(null);
            return;
          }
          if (action === googlePicker.Action.ERROR) {
            const detail = data[googlePicker.Response.ERROR];
            fail(
              new Error(
                detail != null && detail !== ''
                  ? String(detail)
                  : 'Google Picker reported an error.'
              )
            );
            return;
          }
          return;
        } catch (e) {
          fail(e);
        }
      });

    addMyDriveSharedWithMeAndSharedDrivesTabs(builder, googlePicker, {
      mimeTypes: 'application/vnd.google-apps.folder',
      folderPick: true,
      listMode: googlePicker.DocsViewMode.LIST,
    });

    if (appId) builder.setAppId(appId);

    try {
      builder.build().setVisible(true);
    } catch (e) {
      reject(e);
    }
  });
}
